import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import PrivacyMode from "components/Text/PrivacyMode";
import VirtualizedDropdownList from "components/Theme/VirtualizedDropdownList";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
  DropdownSearchInput,
} from "components/Theme/dropdown";
import { useDropdownSearch } from "components/Theme/useDropdownSearch";
import type { Scheme } from "kubb";
import type React from "react";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSelectedCompany } from "../../reducers/company";
import { usePrivacyMode } from "../../reducers/privacy";
import { SchemeContext } from "../../reducers/scheme";
import { getUrlHash } from "../navigation/URL";

// Hoisted so `useDropdownSearch`'s useMemo(filteredItems) sees a stable
// reference and doesn't recompute the filter on every parent render.
const selectSchemeName = (s: Scheme): string => s.scheme_name;

export default function SchemeSelect(): React.ReactElement {
  const company = useSelectedCompany();
  const privacy = usePrivacyMode();

  const { state } = useContext(SchemeContext);
  const { selected, loading, schemes = [] } = state;

  const {
    searchQuery,
    setSearchQuery,
    filteredItems: filteredSchemes,
    ResetSearchOnMount,
  } = useDropdownSearch(schemes, selectSchemeName);

  const navigate = useNavigate();

  const selectScheme = (scheme?: Scheme): void => {
    if (scheme === undefined) {
      navigate({
        hash: getUrlHash(company, scheme, privacy),
      });
    } else if (
      selected?.company_id !== scheme?.company_id ||
      selected?.scheme_id !== scheme?.scheme_id
    ) {
      navigate({
        hash: getUrlHash(company, scheme, privacy),
      });
    }
  };

  const noSchemes = company && !loading && schemes.length === 0;

  return (
    <>
      <Dropdown>
        <DropdownButton
          outline
          data-cy={"scheme-select"}
          disabled={loading || noSchemes || !company}
        >
          <span className={"hidden md:inline-block"}>Scheme: </span>
          <span
            className={
              "text-grey w-auto md:w-32 text-left overflow-clip text-xs md:text-sm text-nowrap"
            }
          >
            {selected ? (
              <PrivacyMode>{selected.scheme_name}</PrivacyMode>
            ) : loading ? (
              <FontAwesomeIcon icon={faSpinner} fixedWidth spin />
            ) : schemes.length > 0 ? (
              "All"
            ) : noSchemes ? (
              "No Schemes"
            ) : (
              "-"
            )}
          </span>
          <ChevronDownIcon />
        </DropdownButton>
        {loading ? (
          <DropdownMenu data-cy={"scheme-select-loading"}>
            <DropdownItem data-cy={"scheme-select-loading"}>
              Loading...
            </DropdownItem>
          </DropdownMenu>
        ) : (
          <DropdownMenu data-cy={"scheme-select-content"}>
            <ResetSearchOnMount />
            <DropdownSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search schemes..."
              aria-label="Search schemes"
            />
            <DropdownItem
              data-cy={"scheme-select-all"}
              onClick={(): void => {
                selectScheme();
              }}
              className={selected === null ? "active" : undefined}
            >
              All
            </DropdownItem>
            <DropdownDivider />
            <VirtualizedDropdownList<Scheme>
              items={filteredSchemes}
              getItemKey={(scheme) => scheme.scheme_id}
              renderLabel={(scheme) => (
                <PrivacyMode>{scheme.scheme_name}</PrivacyMode>
              )}
              onSelect={selectScheme}
              isSelected={(scheme) => scheme.scheme_id === selected?.scheme_id}
              itemTestId={(scheme) => `scheme-select-${scheme.scheme_id}`}
              emptyMessage={searchQuery ? "No results" : "No schemes"}
              aria-label="Schemes"
            />
          </DropdownMenu>
        )}
      </Dropdown>
    </>
  );
}
